/** The longest a browser gets to work out a recording's length. */
const MEASURE_TIMEOUT_MS = 8000;

/**
 * Far enough past any real recording that every browser clamps to the end, which
 * is what makes it work out the true length.
 */
const SEEK_PAST_END = 1e101;

const finiteLength = (audio: HTMLAudioElement): number | undefined =>
  Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : undefined;

/**
 * The length of an audio source in seconds, or undefined when it cannot be told.
 *
 * Browser-recorded WebM/OGG and streamed responses report Infinity once the
 * metadata is loaded, even from an ordinary file server. Seeking past the end
 * makes the browser measure the whole recording and announce the real length.
 * The source is only read, never played, so this is safe beside a player.
 */
export function measureAudioDuration(src: string, timeoutMs: number = MEASURE_TIMEOUT_MS): Promise<number | undefined> {
  return new Promise((resolve) => {
    const audio = new Audio();
    let timer = 0;
    let settled = false;

    const finish = (value?: number) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      audio.removeAttribute('src');
      audio.load();
      resolve(value);
    };

    timer = window.setTimeout(() => finish(undefined), timeoutMs);
    audio.preload = 'metadata';
    audio.onerror = () => finish(undefined);
    audio.ondurationchange = () => {
      const length = finiteLength(audio);
      if (length !== undefined) finish(length);
    };
    audio.onloadedmetadata = () => {
      const length = finiteLength(audio);
      if (length !== undefined) finish(length);
      else audio.currentTime = SEEK_PAST_END;
    };
    audio.src = src;
  });
}
